/**
 * Ed25519 long-term signing keypair for the Super User.
 *
 * Sole use: attest the canonical user-roster pubkeys so a malicious server
 * cannot inject a fake recipient into the recipient list. At user onboarding
 * time, the SU client signs:
 *
 *   message = utf8(user_id) || 0x00 || utf8(ecdh_pub_jwk) || 0x00 || utf8(role)
 *
 * and stores the signature in users.pubkey_sig. Any client that fetches the
 * canonical roster verifies the signature against the SU signing pubkey
 * BEFORE wrapping any item key to that recipient.
 *
 * SU signing pubkey is published once and pinned in the client bundle plus
 * mirrored in users.signing_pub_jwk (server-side cross-check). Loss of the
 * SU signing privkey is a hard recovery operation (rebuild vault).
 *
 * Library: @noble/ed25519 (audited, dep-free, identical API in browser,
 * Node 20+, and Cloudflare Workers).
 */

import * as ed from "@noble/ed25519";
import { sha512 } from "@noble/hashes/sha512";

import { fromBase64Url, toBase64Url, utf8Encode } from "./bytes.js";
import { asBrand } from "../types/index.js";
import type {
  Ed25519PubJwk,
  Ed25519RawPriv,
  Ed25519Signature,
  Role,
  UserId,
  EcdhPubJwk,
} from "../types/index.js";

// @noble/ed25519 v2 requires injecting a SHA-512 implementation for the
// synchronous API; we always use the async API but the injection is still
// required for some code paths. Configure once at module load.
ed.etc.sha512Async = async (...msgs) => sha512(ed.etc.concatBytes(...msgs));
ed.etc.sha512Sync = (...msgs) => sha512(ed.etc.concatBytes(...msgs));

/** Generate a fresh Ed25519 keypair. Returns raw seed + JWK pub. */
export interface Ed25519Keypair {
  /** 32-byte raw private seed. NEVER stored at rest unwrapped. */
  readonly priv: Ed25519RawPriv;
  /** Public key as JWK string ({"kty":"OKP","crv":"Ed25519","x":"..."}). */
  readonly pubJwk: Ed25519PubJwk;
}

function pubBytesToJwk(pubBytes: Uint8Array): Ed25519PubJwk {
  const jwk: JsonWebKey = {
    kty: "OKP",
    crv: "Ed25519",
    x: toBase64Url(pubBytes),
  };
  return JSON.stringify(jwk) as Ed25519PubJwk;
}

function jwkToPubBytes(pubJwk: Ed25519PubJwk): Uint8Array {
  const j = JSON.parse(pubJwk) as JsonWebKey;
  if (j.kty !== "OKP" || j.crv !== "Ed25519" || typeof j.x !== "string") {
    throw new TypeError("jwkToPubBytes: not an Ed25519 OKP JWK");
  }
  return fromBase64Url(j.x);
}

export async function generateSigningKeypair(): Promise<Ed25519Keypair> {
  const priv = ed.utils.randomPrivateKey(); // 32 bytes
  const pub = await ed.getPublicKeyAsync(priv);
  return {
    priv: asBrand<Ed25519RawPriv>(priv),
    pubJwk: pubBytesToJwk(pub),
  };
}

/**
 * Build the canonical message bytes to sign for a recipient.
 * Format: utf8(user_id) || 0x00 || utf8(ecdh_pub_jwk) || 0x00 || utf8(role)
 *
 * The 0x00 delimiter is unambiguous because we explicitly reject components
 * that contain a NUL byte at the input. Without this runtime check a malicious
 * SU-signed payload could be split-recomposed into a different tuple if any
 * component contained NUL bytes.
 */
export class RecipientSigningInputError extends Error {
  public override readonly name = "RecipientSigningInputError";
}

// NOTE: the literal between the double quotes inside assertNoNul is U+0000
// (NUL byte), NOT U+0020 (space). Most code editors and tools render NUL
// invisibly which can look identical to a space. The delimiter we are
// defending against is 0x00 (used in buildRecipientSignMessage below), so
// the check MUST be for NUL specifically. If you ever "correct" this to a
// space thinking the code is broken, you are about to introduce REG-01
// from ARC LOOP iter 2 (see CHANGELOG entry).
function assertNoNul(field: string, value: string): void {
  if (value.indexOf(" ") !== -1) {
    throw new RecipientSigningInputError(
      `buildRecipientSignMessage: ${field} contains NUL byte; canonicalization delimiter would be ambiguous`,
    );
  }
}

export function buildRecipientSignMessage(
  userId: UserId,
  ecdhPubJwk: EcdhPubJwk,
  role: Role,
): Uint8Array {
  assertNoNul("user_id", userId);
  assertNoNul("ecdh_pub_jwk", ecdhPubJwk);
  assertNoNul("role", role);
  const sep = new Uint8Array([0]);
  return new Uint8Array([
    ...utf8Encode(userId),
    ...sep,
    ...utf8Encode(ecdhPubJwk),
    ...sep,
    ...utf8Encode(role),
  ]);
}

export async function signRecipient(
  signingPriv: Ed25519RawPriv,
  userId: UserId,
  ecdhPubJwk: EcdhPubJwk,
  role: Role,
): Promise<Ed25519Signature> {
  const msg = buildRecipientSignMessage(userId, ecdhPubJwk, role);
  const sig = await ed.signAsync(msg, signingPriv);
  return asBrand<Ed25519Signature>(sig);
}

export async function verifyRecipientSignature(
  signingPubJwk: Ed25519PubJwk,
  userId: UserId,
  ecdhPubJwk: EcdhPubJwk,
  role: Role,
  sig: Ed25519Signature,
): Promise<boolean> {
  const msg = buildRecipientSignMessage(userId, ecdhPubJwk, role);
  const pubBytes = jwkToPubBytes(signingPubJwk);
  try {
    return await ed.verifyAsync(sig, msg, pubBytes);
  } catch {
    return false;
  }
}

/** Export helpers for JWK <-> raw conversions. */
export { pubBytesToJwk, jwkToPubBytes };
